import { AxiosResponse } from 'axios';

import { APIResponseStatus } from '../../api/types';
import baseService from '../../store/auth/services/baseServices';
import { ObjectStringIndex } from '../../types';
import { API } from '../../utils/axios';
import { MessagesResponse, Message } from '../../features/messages/types';

export const messageService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getPaginatedMessages: builder.query<
      any,
      {
        personId: string;
        total: boolean;
        query?: string;
        limit?: number;
        offset?: number;
        order?: string;
        sort?: string;
        draft?: boolean;
        sent?: boolean;
        favorite?: boolean;
      }
    >({
      keepUnusedDataFor: 5,
      query: (params: ObjectStringIndex) => {
        const body: string = Object.keys(params)
          .map((key: string) => `${key}=${encodeURIComponent(params[key])}`)
          .join('&');
        return {
          url: `v1/resources/MailboxMessage/_search`,
          method: 'POST',
          body,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        };
      },
      transformResponse: (response: { resource: MessagesResponse }) => {
        return response.resource;
      },
      providesTags: ['Messages'],
    }),
    postMessage: builder.mutation<
      APIResponseStatus<Partial<Message>[]>,
      {
        to: Array<fhir4.Reference>;
        subject: string;
        body: string;
        from: fhir4.Reference;
        threadId?: string;
        contentType?: string;
        attachment?: Array<{
          refrerence: string;
          type: string;
          identifier: fhir4.Identifier;
          display: string;
        }>;
      }
    >({
      query: (params) => {
        const messageContent: {
          contentType: string;
          content: string;
          attachment?: Array<{
            refrerence: string;
            type: string;
            identifier: fhir4.Identifier;
            display: string;
          }>;
        } = {
          contentType: params.contentType ? params.contentType : 'text',
          content: params.body,
          attachment: params.attachment || [],
        };

        if (messageContent?.attachment?.length === 0) delete messageContent.attachment;

        const messageResource = {
          elementType: 'Message',
          header: {
            from: params.from,
            to: params.to.map((ref) => ({ type: 'To', recipient: ref })),
            subject: params.subject,
            x: {},
            threadId: params.threadId,
          },
          body: messageContent,
          draft: false,
          favorite: false,
        };

        return {
          url: 'v1/resources/Message',
          method: 'POST',
          body: {
            resource: messageResource,
          },
        };
      },
    }),
  }),
});

export const { useGetPaginatedMessagesQuery, usePostMessageMutation } = messageService;

export const getMessages = (params: {
  personId: string;
  limit: number;
  offset: number;
  abortController?: AbortController;
}) => {
  const data = { ...params, total: true };
  const encodedData = Object.keys(data)
    // @ts-expect-error: fix object type to be string
    .map((key: string) => `${key}=${encodeURIComponent(data[key])}`)
    .join('&');
  return API.post<any, AxiosResponse<MessagesResponse>>(
    `v1/resources/MailboxMessage/_search`,
    encodedData,
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      signal: params.abortController ? params.abortController.signal : undefined,
    },
  ).then((res) => res.data);
};
export const getMessageByMailboxId = (mailboxId: string) => {
  return API.get<any, AxiosResponse<Partial<Message> | any>>(
    `v1/resources/MailboxMessage/${mailboxId}`,
  ).then((res) => res.data);
};

export const updateMailboxMessage = (mailboxId: string) => {
  return API.put<any, AxiosResponse<Partial<Message> | any>>(
    `v1/resources/MailboxMessage/${mailboxId}`,
    {
      resource: {
        favorite: true,
        read: new Date().toISOString(),
      },
    },
  ).then((res) => res.data);
};
